// Library methods
import { useMemo, useState, useCallback, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useAuth0 } from "@auth0/auth0-react";

// MUI Components
import Typography from "@mui/material/Typography";
import Button from "@mui/material/Button";
import Grid from "@mui/material/Grid";
import SearchBox from "../SearchBox";
import DefaultModal from "../DefaultModal";

// Styles
import {
  ModalCustomCancel,
  ModalCustomConfirm,
} from "../../../styles/muiStylesHelper";

// Utils
import { getLocalDate } from "../../../utils/dateHelper";
import { getPatients } from "../../../services/Patient";
import useClinic from "../../../hooks/useClinic";
import { getTruncatedText } from "../../../utils/textHelper";

const SelectPatientModal = ({
  open,
  onClose,
  onConfirm,
  selectedPatient,
  setSelectedPatient,
}) => {
  // internationalization
  const { t } = useTranslation();
  const { getAccessTokenSilently } = useAuth0();

  // clinic context
  const { clinicId, clinicTimezone } = useClinic();

  // state
  const [patientsOptions, setPatientsOptions] = useState(null);

  const getPatientsCall = useCallback(
    async (signal) => {
      try {
        const token = await getAccessTokenSilently();
        const patientsList = await getPatients(token, clinicId, signal);
        setPatientsOptions(patientsList);
      } catch (e) {}
    },
    [clinicId, getAccessTokenSilently]
  );

  useEffect(() => {
    const controller = new AbortController();
    if (open) {
      getPatientsCall(controller.signal);
    }
    return () => {
      controller.abort();
    };
  }, [getPatientsCall, open]);

  const formattedPatientsOptions = useMemo(
    () =>
      (patientsOptions ?? []).map((option) => {
        const patientName = getTruncatedText(
          `${option?.firstName} ${option?.lastName}`
        );
        const birthdate = getLocalDate(option?.dateOfBirth, clinicTimezone);
        return {
          ...option,
          name: `${patientName} / ${birthdate}`,
        };
      }),
    [patientsOptions, clinicTimezone]
  );

  const shouldDisable = useMemo(() => {
    // disable the button if the selected patient isn't a valid dropdown option
    return !(patientsOptions ?? []).some(
      (patient) => patient?.id === selectedPatient?.id
    );
  }, [patientsOptions, selectedPatient?.id]);

  return (
    <DefaultModal
      isOpen={open}
      onClose={onClose}
      title={t("select_patient")}
      maxWidth="xs"
      modalActions={
        <Grid
          sx={{
            display: "flex",
            justifyContent: "flex-end",
            gap: "20px",
          }}
        >
          <Button variant="text" sx={ModalCustomCancel} onClick={onClose}>
            {t("button_cancel")}
          </Button>
          <Button
            variant="outlined"
            sx={ModalCustomConfirm}
            disabled={shouldDisable}
            onClick={onConfirm}
          >
            {t("button_confirm")}
          </Button>
        </Grid>
      }
    >
      <Grid display="flex" flexDirection="column" p="20px" gap="20px">
        <Typography>{t("select_patient_text")}:</Typography>
        {/* Device selector */}
        <SearchBox
          data={formattedPatientsOptions}
          state={selectedPatient}
          setState={setSelectedPatient}
          label={t("current_patient")}
          propertyBit={4}
          mb={0}
        />
      </Grid>
    </DefaultModal>
  );
};

export default SelectPatientModal;
